import React, { useCallback, useEffect, useState } from 'react';

import { ValidationStatus, Validator } from 'types/validator';

interface AutoCompleterProps {
    searchValue?: string;
    validatorList: Array<Validator<string>>;
    onSearch?: (value: string) => void;
    onSearchInputClick?: () => void;
    onBlur?: () => void;
    ref?: React.RefObject<HTMLInputElement>;
}

export const useAutocompleter = ({
    onSearch,
    onSearchInputClick,
    onBlur,
    validatorList,
    searchValue,
    ref,
}: AutoCompleterProps) => {
    const [isClearButtonVisible, setIsClearButtonVisible] = useState(false);
    const [cursor, setCursor] = useState(0);
    const [isUserInput, setIsUserInput] = useState(false);

    const onValidation = useCallback(
        async (value?: string) => {
            // if no validatorList, ignore validation
            if (!validatorList.length) {
                return true;
            }

            try {
                // check all validators and cancel validation, if one fails
                for (const validator of validatorList) {
                    // check ValidationStatus and execute possible callbacks
                    const status = await validator.validationMethod(value);
                    switch (status) {
                        case ValidationStatus.Success:
                            validator.validCallback?.(value);
                            break;
                        default:
                            validator.errorCallback?.(value);
                            return false; // stop loop
                    }
                }
                return true;
            } catch (error) {
                throw new Error(`error in validation: ${error}`);
            }
        },
        [validatorList]
    );

    const onChange = useCallback(
        async (value: string) => {
            setIsClearButtonVisible(value.length > 0);
            const isValid = await onValidation(value);
            if (isValid) {
                onSearch?.(value);
            }
        },
        [onSearch, onValidation]
    );

    const onClick = useCallback(() => {
        if (!isUserInput) {
            setIsUserInput(true);
            onSearchInputClick?.();
        }
    }, [onSearchInputClick, isUserInput, setIsUserInput]);

    const onBlur_ = useCallback(() => {
        setIsUserInput(false);
        onBlur?.();
    }, [onBlur]);

    const handleCursor = useCallback(
        (target: EventTarget & HTMLInputElement) => {
            setCursor(target.selectionStart ?? 0);
        },
        []
    );

    useEffect(() => {
        if (isUserInput) {
            ref?.current?.setSelectionRange(cursor, cursor);
        }
    }, [cursor, isUserInput, ref, searchValue]);

    return {
        isClearButtonVisible,
        setIsClearButtonVisible,
        onChange,
        onClick,
        onBlur_,
        handleCursor,
    };
};
